import { type IPosition } from "@sc07-canvas/lib/net";
import {
  MOUSE_LONG_PRESS_DELAY,
  PanZoomWrapper,
  RendererContext,
  type ViewportMoveEvent,
} from "@sc07-canvas/lib/renderer";
import throttle from "lodash.throttle";
import {
  type CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useCaptureContext } from "@/contexts/CaptureContext";
import { usePanel } from "@/contexts/PanelContext";

import { useAppContext } from "../contexts/AppContext";
import { useTemplateContext } from "../contexts/TemplateContext";
import { useHasRole } from "../hooks/useHasRole";
import { useIsMobile } from "../hooks/useIsMobile";
import { CanvasCore } from "../lib/canvas";
import { CanvasUtils } from "../lib/canvas.utils";
import { KeybindManager } from "../lib/keybinds";
import { PaletteLib } from "../lib/palette";
import { type IRouterData, Router } from "../lib/router";
import { Template as TemplateCl } from "../lib/template";
import { getRenderer } from "../lib/utils";
import { ModCanvasOverlay } from "../Moderator/ModCanvasOverlay";
import {
  showColorDeselectFeedback,
  showColorPickFeedback,
  showPixelPlacedFeedback,
} from "./ColorPickFeedback";
import { CursorPresenceLayer } from "./CursorPresenceLayer";
import { GridOverlay } from "./Overlay/GridOverlay";
import { HeatmapOverlay } from "./Overlay/HeatmapOverlay";
import { PixelPulses } from "./Overlay/PixelPulses";
import { Template } from "./Templating/Template";

const KEYBOARD_ZOOM_STEP = 1;
const KEYBOARD_ZOOM_SMALL_STEP = 0.35;
const INSPECT_END_ANIMATION_DURATION = 180;
const TOUCH_INSPECT_MOVE_THRESHOLD = 25;

export const CanvasWrapper = () => {
  const hasMod = useHasRole("MOD");
  const isMobile = useIsMobile();
  const { config, setCanvasPosition } = useAppContext();
  const initialPositionSet = useRef(false);

  useLayoutEffect(() => {
    if (!config || initialPositionSet.current) return;

    initialPositionSet.current = true;
    const route = Router.get().canvas;

    setCanvasPosition({
      x: route?.x ?? config.canvas.size[0] >> 1,
      y: route?.y ?? config.canvas.size[1] >> 1,
      zoom: route ? route.zoom || 0 : 1,
    });
  }, [config, setCanvasPosition]);

  const getInitialPosition = useCallback<
    (useCssZoom: boolean) =>
      | {
          x: number;
          y: number;
          zoom?: number;
        }
      | undefined
  >(
    (_useCssZoom) => {
      const router = Router.get().canvas;

      if (!router) return undefined;
      if (!config) {
        console.warn("getInitialPosition called with no config");
        return undefined;
      }

      const { transformX, transformY } = CanvasUtils.canvasToPanZoomTransform(
        router.x,
        router.y,
        config.canvas.size,
        // at one point we needed to pass if we were using css zoom
        // useCssZoom,
      );

      return {
        x: transformX,
        y: transformY,
        zoom: router.zoom,
      };
    },
    [config],
  );

  return (
    <main>
      <PanZoomWrapper initialPosition={getInitialPosition}>
        {hasMod && <ModCanvasOverlay />}
        {/* <BlankOverlay /> */}
        <HeatmapOverlay />
        <GridOverlay />
        <PixelPulses />
        {config && <Template />}
        <CanvasInner />
        <CursorInspectPreview />
        {!isMobile && config?.cursors.enabled && (
          <CursorPresenceLayer />
        )}
      </PanZoomWrapper>
    </main>
  );
}

const CursorInspectPreview = () => {
  const { cursor } = useAppContext();
  const PanZoom = useContext(RendererContext);
  const selectedColor =
    typeof cursor.color === "number"
      ? PaletteLib.getColor(cursor.color)?.hex
      : undefined;
  const hasSelectedColor = Boolean(
    selectedColor && selectedColor !== "transparent",
  );
  const color = hasSelectedColor ? selectedColor!.replace(/^#/, "") : "ffffff";
  const [inspectHold, setInspectHold] = useState<
    "idle" | "holding" | "ready" | "ending"
  >("idle");
  const [inspectPosition, setInspectPosition] = useState<
    { x: number; y: number } | undefined
  >(undefined);
  const [, setInspectProjectionVersion] = useState(0);
  const inspectPointerDown = useRef<
    { clientX: number; clientY: number } | undefined
  >(undefined);
  const inspectElement = useRef<HTMLDivElement>(null);
  const inspectStartedAt = useRef(0);
  const inspectEndTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let touchInspectPointerId: number | undefined;
    let touchInspectStart: { clientX: number; clientY: number } | undefined;
    let touchInspectReadyTimer: number | undefined;

    const clearEndTimer = () => {
      if (inspectEndTimer.current === undefined) return;
      window.clearTimeout(inspectEndTimer.current);
      inspectEndTimer.current = undefined;
    };
    const updateInspectPosition = () => {
      const position = inspectPointerDown.current;
      const canvas = CanvasCore.get(false);
      if (!canvas || !position) return;
      const [x, y] = canvas.screenToPos(position.clientX, position.clientY);
      setInspectPosition({ x, y });
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.button !== 0 ||
        !(event.target instanceof Element) ||
        !event.target.closest(".board-wrapper")
      ) {
        return;
      }
      inspectPointerDown.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
    };
    const handleStart = () => {
      clearEndTimer();
      inspectStartedAt.current = performance.now();
      updateInspectPosition();
      setInspectHold("holding");
    };
    const handleReady = () => {
      clearEndTimer();
      updateInspectPosition();
      setInspectHold("ready");
    };
    const handleEnd = () => {
      clearEndTimer();
      const elapsed = performance.now() - inspectStartedAt.current;
      const showedInspectUi = elapsed >= MOUSE_LONG_PRESS_DELAY * 0.2;

      if (!showedInspectUi) {
        setInspectHold("idle");
        setInspectPosition(undefined);
        return;
      }

      const element = inspectElement.current;
      if (element) {
        const surface = getComputedStyle(element);
        const fill = getComputedStyle(element, "::before");
        const question = getComputedStyle(element, "::after");
        element.style.setProperty("--inspect-release-opacity", surface.opacity);
        element.style.setProperty(
          "--inspect-release-shadow",
          surface.boxShadow,
        );
        element.style.setProperty("--inspect-release-fill-height", fill.height);
        element.style.setProperty(
          "--inspect-release-fill-opacity",
          fill.opacity,
        );
        element.style.setProperty(
          "--inspect-release-question-transform",
          question.transform,
        );
        element.style.setProperty(
          "--inspect-release-question-opacity",
          question.opacity,
        );
      }

      setInspectHold("ending");
      inspectEndTimer.current = window.setTimeout(() => {
        inspectEndTimer.current = undefined;
        setInspectHold("idle");
        setInspectPosition(undefined);
      }, INSPECT_END_ANIMATION_DURATION);
    };
    const clearTouchInspectReadyTimer = () => {
      if (touchInspectReadyTimer === undefined) return;
      window.clearTimeout(touchInspectReadyTimer);
      touchInspectReadyTimer = undefined;
    };
    const endTouchInspect = () => {
      if (touchInspectPointerId === undefined) return;
      clearTouchInspectReadyTimer();
      touchInspectPointerId = undefined;
      touchInspectStart = undefined;
      handleEnd();
    };
    const handleTouchPointerDown = (event: PointerEvent) => {
      if (
        event.pointerType !== "touch" ||
        event.button !== 0 ||
        !(event.target instanceof Element) ||
        !event.target.closest(".board-wrapper")
      ) {
        return;
      }

      if (touchInspectPointerId !== undefined) {
        endTouchInspect();
        return;
      }

      touchInspectPointerId = event.pointerId;
      touchInspectStart = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      handleStart();
      touchInspectReadyTimer = window.setTimeout(() => {
        touchInspectReadyTimer = undefined;
        if (touchInspectPointerId === event.pointerId) handleReady();
      }, MOUSE_LONG_PRESS_DELAY);
    };
    const handleTouchPointerMove = (event: PointerEvent) => {
      if (
        event.pointerId !== touchInspectPointerId ||
        !touchInspectStart
      ) {
        return;
      }

      const distance = Math.max(
        Math.abs(event.clientX - touchInspectStart.clientX),
        Math.abs(event.clientY - touchInspectStart.clientY),
      );
      if (distance >= TOUCH_INSPECT_MOVE_THRESHOLD) endTouchInspect();
    };
    const handleTouchPointerEnd = (event: PointerEvent) => {
      if (event.pointerId === touchInspectPointerId) endTouchInspect();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointerdown", handleTouchPointerDown, true);
    document.addEventListener("pointermove", handleTouchPointerMove, true);
    document.addEventListener("pointerup", handleTouchPointerEnd, true);
    document.addEventListener("pointercancel", handleTouchPointerEnd, true);
    PanZoom.on("inspectHoldStart", handleStart);
    PanZoom.on("inspectHoldReady", handleReady);
    PanZoom.on("inspectHoldEnd", handleEnd);

    return () => {
      clearEndTimer();
      clearTouchInspectReadyTimer();
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointerdown", handleTouchPointerDown, true);
      document.removeEventListener("pointermove", handleTouchPointerMove, true);
      document.removeEventListener("pointerup", handleTouchPointerEnd, true);
      document.removeEventListener(
        "pointercancel",
        handleTouchPointerEnd,
        true,
      );
      PanZoom.off("inspectHoldStart", handleStart);
      PanZoom.off("inspectHoldReady", handleReady);
      PanZoom.off("inspectHoldEnd", handleEnd);
    };
  }, [PanZoom]);

  useEffect(() => {
    if (inspectHold === "idle" && !hasSelectedColor) return;

    let projectionFrame: number | undefined;
    const refreshProjection = () => {
      if (projectionFrame !== undefined) return;

      projectionFrame = window.requestAnimationFrame(() => {
        projectionFrame = undefined;
        setInspectProjectionVersion((version) => version + 1);
      });
    };

    document.addEventListener("pointermove", refreshProjection, true);
    PanZoom.on("viewportMove", refreshProjection);

    return () => {
      if (projectionFrame !== undefined) {
        window.cancelAnimationFrame(projectionFrame);
      }
      document.removeEventListener("pointermove", refreshProjection, true);
      PanZoom.off("viewportMove", refreshProjection);
    };
  }, [PanZoom, hasSelectedColor, inspectHold]);

  const previewPosition = inspectPosition ?? cursor;
  const inspectScreenRect = (() => {
    if (
      (inspectHold === "idle" && !hasSelectedColor) ||
      previewPosition.x < 0 ||
      previewPosition.y < 0
    ) {
      return undefined;
    }

    try {
      return CanvasCore.get().canvasAreaToScreenRect({
        x: previewPosition.x,
        y: previewPosition.y,
        width: 1,
        height: 1,
      });
    } catch {
      return undefined;
    }
  })();
  const inspectBorderSize = inspectScreenRect
    ? Math.min(inspectScreenRect.width, inspectScreenRect.height)
    : 1;

  const preview = (
    <div
      ref={inspectElement}
      className={[
        "noselect canvas-cursor-preview",
        !hasSelectedColor && "is-no-color",
        (inspectHold === "holding" || inspectHold === "ready") &&
          "is-inspect-holding",
        inspectHold === "ready" && "is-inspect-ready",
        inspectHold === "ending" && "is-inspect-ending",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        {
          top: previewPosition.y,
          left: previewPosition.x,
          ...(inspectScreenRect && {
            position: "fixed",
            top: inspectScreenRect.top,
            left: inspectScreenRect.left,
            width: inspectScreenRect.width,
            height: inspectScreenRect.height,
            zIndex: 1,
            "--inspect-border-start-inner": inspectBorderSize * 0.02 + "px",
            "--inspect-border-start-outer": inspectBorderSize * 0.04 + "px",
            "--inspect-border-inner": inspectBorderSize * 0.07 + "px",
            "--inspect-border-outer": inspectBorderSize * 0.14 + "px",
            "--inspect-question-offset": inspectBorderSize * 0.02 + "px",
          }),
          "--cursor-preview-color": "#" + color,
          "--inspect-hold-delay": MOUSE_LONG_PRESS_DELAY * 0.2 + "ms",
          "--inspect-hold-fill-duration": MOUSE_LONG_PRESS_DELAY * 0.7 + "ms",
          "--inspect-hold-surface-duration":
            MOUSE_LONG_PRESS_DELAY * 0.27 + "ms",
          "--inspect-end-duration": INSPECT_END_ANIMATION_DURATION + "ms",
        } as CSSProperties
      }
    />
  );

  return inspectScreenRect && typeof document !== "undefined"
    ? createPortal(preview, document.body)
    : preview;
};

const CanvasInner = () => {
  const canvasSkeleton = useRef<HTMLDivElement>(null);
  const canvas = useRef<CanvasCore>(null);
  const { config, settings, blankOverlay, setCanvasPosition, setCursorPos } =
    useAppContext();
  const Panel = usePanel();
  const { enable: templateEnable } = useTemplateContext();
  const PanZoom = useContext(RendererContext);
  const { captureCanvas, captureView, captureRegion, captureTemplateArea } =
    useCaptureContext();

  useEffect(() => {
    PanZoom.setup.zoomSensitivity = settings.get["canvas.zoomSensitivity"];
  }, [PanZoom, settings.get["canvas.zoomSensitivity"]]);

  useEffect(() => {
    const heldKeys = new Set<string>();
    let animationFrame: number | undefined;
    let previousTime: number | undefined;
    let velocityX = 0;
    let velocityY = 0;
    let viewportChanged = false;
    const panDirections = new Map<string, readonly [number, number]>();
    const addDirection = (
      binding: "PAN_UP" | "PAN_DOWN" | "PAN_LEFT" | "PAN_RIGHT",
      direction: readonly [number, number],
    ) => {
      for (const { key } of KeybindManager.getKeybind(binding)) {
        panDirections.set(key, direction);
      }
    };

    addDirection("PAN_UP", [0, 1]);
    addDirection("PAN_DOWN", [0, -1]);
    addDirection("PAN_LEFT", [1, 0]);
    addDirection("PAN_RIGHT", [-1, 0]);

    const snapToDevicePixel = (position: number) => {
      const screenScale = PanZoom.transform.scale * window.devicePixelRatio;
      return Math.round(position * screenScale) / screenScale;
    };

    const isTypingTarget = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      Boolean(
        target.closest(
          'input, textarea, select, [contenteditable="true"], [role="textbox"]',
        ),
      );

    const moveViewport = (time: number) => {
      const elapsed = Math.min((time - (previousTime ?? time)) / 1000, 0.05);
      previousTime = time;

      const canMove = KeybindManager.isEnabled() && PanZoom.panning.enabled;
      let horizontal = 0;
      let vertical = 0;

      if (canMove) {
        for (const code of heldKeys) {
          const [x, y] = panDirections.get(code) ?? [0, 0];
          horizontal += x;
          vertical += y;
        }
      }
      const diagonalScale = horizontal && vertical ? Math.SQRT1_2 : 1;
      const targetVelocityX = horizontal * 360 * diagonalScale;
      const targetVelocityY = vertical * 360 * diagonalScale;
      const easing = 1 - Math.exp(-12 * elapsed);

      velocityX += (targetVelocityX - velocityX) * easing;
      velocityY += (targetVelocityY - velocityY) * easing;

      if (Math.abs(velocityX) > 0.5 || Math.abs(velocityY) > 0.5) {
        PanZoom.transform.x += (velocityX * elapsed) / PanZoom.transform.scale;
        PanZoom.transform.y += (velocityY * elapsed) / PanZoom.transform.scale;
        const visualX = snapToDevicePixel(PanZoom.transform.x);
        const visualY = snapToDevicePixel(PanZoom.transform.y);
        PanZoom.$move.style.setProperty(
          "transform",
          `translate3d(${visualX}px, ${visualY}px, 0)`,
        );
        viewportChanged = true;
        animationFrame = requestAnimationFrame(moveViewport);
      } else if (heldKeys.size && canMove) {
        animationFrame = requestAnimationFrame(moveViewport);
      } else {
        velocityX = 0;
        velocityY = 0;
        animationFrame = undefined;
        previousTime = undefined;

        if (viewportChanged) {
          viewportChanged = false;
          PanZoom.transform.x = snapToDevicePixel(PanZoom.transform.x);
          PanZoom.transform.y = snapToDevicePixel(PanZoom.transform.y);
          PanZoom.update();
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !KeybindManager.isEnabled() ||
        !panDirections.has(event.code) ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        !PanZoom.panning.enabled ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      heldKeys.add(event.code);
      if (animationFrame === undefined) {
        animationFrame = requestAnimationFrame(moveViewport);
      }
      event.preventDefault();
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (heldKeys.delete(event.code)) event.preventDefault();
    };

    const stopMoving = () => {
      heldKeys.clear();
      velocityX = 0;
      velocityY = 0;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", stopMoving);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", stopMoving);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
    };
  }, [PanZoom]);

  /**
   * Is the canvas coordinate within the bounds of the canvas?
   */
  const isCoordInCanvas = useCallback(
    (x: number, y: number): boolean => {
      if (!canvas.current) {
        console.warn(
          "[CanvasWrapper#isCoordInCanvas] canvas instance does not exist",
        );
        return false;
      }

      if (x < 0 || y < 0) return false; // not positive, impossible to be on canvas

      // canvas size can dynamically change, so we need to check the current config
      // we're depending on canvas.instance's config so we don't have to use a react dependency
      if (canvas.current.hasConfig()) {
        const {
          canvas: {
            size: [width, height],
          },
        } = canvas.current.getConfig();

        if (x >= width || y >= height) return false; // out of bounds
      } else {
        // although this should never happen, log it
        console.warn(
          "[CanvasWrapper#isCoordInCanvas] canvas config is not available yet",
        );
      }

      return true;
    },
    [canvas.current],
  );

  const handlePixelWhois = useCallback(
    ({ clientX, clientY }: { clientX: number; clientY: number }) => {
      if (!canvas.current) {
        console.warn(
          "[CanvasWrapper#handlePixelWhois] canvas instance does not exist",
        );
        return;
      }

      const [x, y] = canvas.current.screenToPos(clientX, clientY);
      if (!isCoordInCanvas(x, y)) return; // out of bounds

      // .......
      // .......
      // .......
      // ...x...
      // .......
      // .......
      // .......
      const surrounding = canvas.current.getSurroundingPixels(x, y, 3);

      Panel.dispatch([
        "whois",
        { x, y, surrounding, requestId: crypto.randomUUID() },
      ]);
    },
    [canvas.current],
  );

  const getTemplatePixelColor = useCallback(
    (clientX: number, clientY: number) => {
      if (!templateEnable) return;
      return TemplateCl.instance?.getPaletteColorAt(clientX, clientY);
    },
    [templateEnable],
  );

  const handlePickPixel = useCallback(
    ({ clientX, clientY }: { clientX: number; clientY: number }) => {
      if (!canvas.current) {
        console.warn(
          "[CanvasWrapper#handlePickPixel] canvas instance does not exist",
        );
        return;
      }

      const [x, y] = canvas.current.screenToPos(clientX, clientY);
      const templateColor = getTemplatePixelColor(clientX, clientY);

      if (templateColor === undefined && !isCoordInCanvas(x, y)) {
        PaletteLib.setSelectedColor(undefined);
        showColorDeselectFeedback(clientX, clientY);
        return;
      }

      let pixelColor = templateColor ?? -1;

      if (pixelColor === -1) {
        pixelColor = canvas.current.getPixel(x, y)?.color ?? -1;
      }

      if (pixelColor === -1) {
        pixelColor = PaletteLib.getColorFromHex("ffffff")?.id ?? -1;
      }

      if (pixelColor === -1) return;

      PaletteLib.setSelectedColor(pixelColor);
      showColorPickFeedback(
        clientX,
        clientY,
        PaletteLib.getColor(pixelColor)?.hex,
      );
    },
    [getTemplatePixelColor, isCoordInCanvas],
  );

  useEffect(() => {
    // ensure CanvasCore is instanciated
    canvas.current = CanvasCore.get();
    const handlePixelPlaced = ({
      clientX,
      clientY,
      color,
    }: {
      clientX: number;
      clientY: number;
      color: number;
    }) =>
      showPixelPlacedFeedback(
        clientX,
        clientY,
        PaletteLib.getColor(color)?.hex,
      );

    canvas.current.on("pixelPlaced", handlePixelPlaced);
    canvas.current.on("canvasReady", () => {
      console.log("[CanvasWrapper] received canvasReady");
    });

    return () => {
      canvas.current?.off("pixelPlaced", handlePixelPlaced);
      canvas.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const zoomBy = (step: number) => {
      if (!PanZoom.$zoom || !PanZoom.$move) return;

      const oldScale = PanZoom.transform.scale;
      PanZoom.nudgeScale(step * PanZoom.setup.zoomSensitivity);

      if (oldScale !== PanZoom.transform.scale) PanZoom.update();
    };

    const zoomOut = () => zoomBy(-KEYBOARD_ZOOM_STEP);
    const zoomIn = () => zoomBy(KEYBOARD_ZOOM_STEP);
    const zoomOutSmall = () => zoomBy(-KEYBOARD_ZOOM_SMALL_STEP);
    const zoomInSmall = () => zoomBy(KEYBOARD_ZOOM_SMALL_STEP);

    KeybindManager.on("PIXEL_WHOIS", handlePixelWhois);
    KeybindManager.on("PICK_CANVAS_COLOR", handlePickPixel);
    KeybindManager.on("CAPTURE_CANVAS", captureCanvas);
    KeybindManager.on("CAPTURE_VIEW", captureView);
    KeybindManager.on("CAPTURE_REGION", captureRegion);
    KeybindManager.on("CAPTURE_TEMPLATE", captureTemplateArea);
    KeybindManager.on("ZOOM_OUT", zoomOut);
    KeybindManager.on("ZOOM_IN", zoomIn);
    KeybindManager.on("ZOOM_OUT_SMALL", zoomOutSmall);
    KeybindManager.on("ZOOM_IN_SMALL", zoomInSmall);

    return () => {
      KeybindManager.off("PIXEL_WHOIS", handlePixelWhois);
      KeybindManager.off("PICK_CANVAS_COLOR", handlePickPixel);
      KeybindManager.off("CAPTURE_CANVAS", captureCanvas);
      KeybindManager.off("CAPTURE_VIEW", captureView);
      KeybindManager.off("CAPTURE_REGION", captureRegion);
      KeybindManager.off("CAPTURE_TEMPLATE", captureTemplateArea);
      KeybindManager.off("ZOOM_OUT", zoomOut);
      KeybindManager.off("ZOOM_IN", zoomIn);
      KeybindManager.off("ZOOM_OUT_SMALL", zoomOutSmall);
      KeybindManager.off("ZOOM_IN_SMALL", zoomInSmall);
    };
  }, [
    PanZoom,
    handlePixelWhois,
    handlePickPixel,
    captureCanvas,
    captureView,
    captureRegion,
    captureTemplateArea,
  ]);

  useEffect(() => {
    canvas.current?.setPanZoom(PanZoom);
    Router.PanZoom = PanZoom;
  }, [PanZoom]);

  useEffect(() => {
    if (!canvas.current) {
      console.warn("canvas isntance doesn't exist");
      return;
    }

    const handleCursorPos = throttle((pos: IPosition) => {
      if (!canvas.current?.hasConfig() || !config) {
        console.warn("handleCursorPos has no config");
        return;
      }

      if (
        pos.x < 0 ||
        pos.y < 0 ||
        pos.x > config.canvas.size[0] ||
        pos.y > config.canvas.size[1]
      ) {
        setCursorPos(undefined, undefined);
      } else {
        // fixes not passing the current value
        setCursorPos(pos.x, pos.y);
      }
    }, 1);

    canvas.current.on("cursorPos", handleCursorPos);

    return () => {
      canvas.current!.off("cursorPos", handleCursorPos);
    };
  }, [config, setCursorPos]);

  useEffect(() => {
    if (!canvas.current) {
      console.warn("canvasinner config received but no canvas instance");
      return;
    }
    if (!config) {
      console.warn("canvasinner config received falsey");
      return;
    }

    console.log("[CanvasInner] config updated, informing canvas instance");
    canvas.current.loadConfig(config);
  }, [config]);

  const handleNavigate = useCallback(
    (data: IRouterData) => {
      if (data.canvas) {
        const position = canvas.current!.canvasToPanZoomTransform(
          data.canvas.x,
          data.canvas.y,
        );

        PanZoom.setPosition(
          {
            x: position.transformX,
            y: position.transformY,
            zoom: data.canvas.zoom || 0, // TODO: fit canvas to viewport instead of defaulting
          },
          { suppressEmit: true },
        );
      }
    },
    [PanZoom],
  );

  useEffect(() => {
    // if (!config?.canvas || !canvasRef.current) return;
    // const canvas = canvasRef.current!;
    // const canvasInstance = new Canvas(canvas, PanZoom);
    const initAt = Date.now();

    // initial position from Router is setup in <CanvasWrapper>

    const handleViewportMove = (state: ViewportMoveEvent) => {
      if (Date.now() - initAt < 60 * 1000) {
        console.debug(
          "[CanvasWrapper] handleViewportMove called soon after init",
          Date.now() - initAt,
        );
      }

      if (canvas.current) {
        const pos = canvas.current.panZoomTransformToCanvas();
        setCanvasPosition({
          x: pos.canvasX,
          y: pos.canvasY,
          zoom: state.scale >> 0,
        });
      } else {
        console.warn(
          "[CanvasWrapper] handleViewportMove has no canvas instance",
        );
      }

      Router.queueUpdate();
    };

    PanZoom.addListener("viewportMove", handleViewportMove);
    Router.on("navigate", handleNavigate);

    return () => {
      PanZoom.removeListener("viewportMove", handleViewportMove);
      Router.off("navigate", handleNavigate);
    };
  }, [PanZoom, setCanvasPosition]);

  useEffect(() => {
    const $blankOverlay = document.getElementById("blank-overlay");
    if (!$blankOverlay) return;

    $blankOverlay.style.display = blankOverlay.enabled ? "block" : "none";
    $blankOverlay.style.opacity = blankOverlay.opacity.toFixed(1);
  }, [blankOverlay]);

  useEffect(() => {
    if (!canvasSkeleton.current) {
      console.error("canvasskeleton is null");
      return;
    }

    // creates the canvas upon mounting
    // and destroys upon unmount
    // this ensures the context hasn't already been used

    const $skeleton = canvasSkeleton.current;

    // core canvas
    const $canvas = document.createElement("canvas");
    $canvas.setAttribute("id", "board");
    $canvas.setAttribute("width", "1000");
    $canvas.setAttribute("height", "1000");
    $canvas.classList.add("pixelate");
    $skeleton.parentElement?.insertBefore($canvas, $skeleton);
    CanvasCore.get().setCanvas($canvas);

    // blank canvas
    const $blank = document.createElement("canvas");
    $blank.setAttribute("id", "blank-overlay");
    $blank.classList.add("board-overlay", "no-interact", "pixelate");
    $blank.setAttribute("width", "1000");
    $blank.setAttribute("height", "1000");
    $blank.style.display = "none";
    $skeleton.parentElement?.insertBefore($blank, $skeleton);
    getRenderer().useCanvas($blank, "blank");

    return () => {
      CanvasCore.get(false)?.setCanvas(null);
      $canvas.remove();
      getRenderer().removeCanvas("blank");
      $blank.remove();
    };
  }, []);

  return <div data-canvas-skeleton ref={canvasSkeleton}></div>;
};
